import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, ChevronRight, CreditCard } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { formatCents, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import { PO_STATUS_BADGE } from '@/lib/badges';
import { LinkEstimateToPoModal } from '@/components/LinkEstimateToPoModal';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface PurchaseOrderRow {
  id: number;
  number: string;
  supplier_id: number;
  supplier_name: string;
  source_estimate_id: number | null;
  source_estimate_number: string | null;
  order_date: string;
  expected_date: string | null;
  status: POStatus;
  total: number;
  amount_paid: number;
  created_at: string;
}

// ─── Module-level constants ─────────────────────────────────────────────────────

type POStatus = 'draft' | 'sent' | 'received' | 'returned' | 'cancelled';
type StatusFilter = 'all' | POStatus;

const STATUS_TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Received', value: 'received' },
  { label: 'Returned', value: 'returned' },
  { label: 'Cancelled', value: 'cancelled' },
];

/** Statuses where edit is permitted (admin only) */
const EDITABLE_STATUSES: POStatus[] = ['draft', 'sent'];

// ─── Delete Confirm Dialog ───────────────────────────────────────────────────────

interface DeleteDialogProps {
  po: PurchaseOrderRow;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteDialog({ po, onCancel, onConfirm, loading }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Purchase Order</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete PO <span className="font-semibold text-foreground">{po.number}</span>? If
            received, this reverses stock. This cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────────

export default function PurchaseOrdersPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<StatusFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [deletingPO, setDeletingPO] = useState<PurchaseOrderRow | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [linkEstFor, setLinkEstFor] = useState<PurchaseOrderRow | null>(null);

  const {
    rows: purchaseOrders,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
  } = usePaginatedList<PurchaseOrderRow>({
    endpoint: '/api/purchase-orders',
    entityKey: 'purchase_orders',
    defaultSort: 'date',
    defaultDir: 'desc',
    extraParams: {
      status: status !== 'all' ? status : undefined,
      from: from || undefined,
      to: to || undefined,
    },
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
  }

  async function handleDelete() {
    if (!deletingPO) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/purchase-orders/${deletingPO.id}`, { method: 'DELETE' });
      toast.success('Purchase order deleted');
      setDeletingPO(undefined);
      refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete purchase order';
      toast.error(msg);
      setDeletingPO(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Purchase Orders</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            endpoint="/api/purchase-orders"
            params={{
              status: status !== 'all' ? status : undefined,
              q: q || undefined,
              from: from || undefined,
              to: to || undefined,
            }}
          />
          <Link to="/po-payments">
            <Button variant="outline" size="sm">
              <CreditCard className="h-4 w-4 mr-1.5" />
              View payments
            </Button>
          </Link>
          <Button size="sm" onClick={() => navigate('/purchase-orders/new')}>
            <Plus className="h-4 w-4 mr-1.5" />
            New PO
          </Button>
        </div>
      </div>

      {/* Filters — one line: search · date range · status pills (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="PO # or supplier…"
            className="pl-9 h-9"
          />
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2 text-sm shrink-0">
          <span className="text-muted-foreground">From</span>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          <span className="text-muted-foreground">To</span>
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-40 h-9 text-sm"
          />
          {(from || to) && (
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
            >
              Clear
            </button>
          )}
        </div>

        {/* Status pills */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 shrink-0">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Number"
                sortKey="number"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-32"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground">Supplier</th>
              <SortableTh
                label="Order Date"
                sortKey="date"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="hidden sm:table-cell w-28"
              />
              <SortableTh
                label="Total"
                sortKey="total"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28 text-right"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground w-28 hidden md:table-cell">
                Paid
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground w-28 hidden md:table-cell">
                Balance
              </th>
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-24"
              />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground w-32 hidden md:table-cell">
                Linked Estimate
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && purchaseOrders.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">
                  No purchase orders found.
                </td>
              </tr>
            )}
            {purchaseOrders.map((po) => {
              const badge = PO_STATUS_BADGE[po.status];
              const balance = po.total - po.amount_paid;
              // Operators may edit DRAFT POs; SENT stays admin-only.
              const canEdit =
                EDITABLE_STATUSES.includes(po.status) && (po.status === 'draft' || isAdmin);
              return (
                <tr key={po.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3 font-medium">
                    <Link
                      to={`/purchase-orders/${po.id}`}
                      className="font-mono text-xs hover:underline underline-offset-4"
                    >
                      {po.number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/suppliers/${po.supplier_id}`}
                      className="hover:underline underline-offset-4 text-muted-foreground"
                    >
                      {po.supplier_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell whitespace-nowrap">
                    {formatDate(po.order_date)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{formatCents(po.total)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-muted-foreground hidden md:table-cell">
                    {formatCents(po.amount_paid)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums hidden md:table-cell">
                    {balance > 0 ? (
                      <span className="text-amber-700 font-medium">{formatCents(balance)}</span>
                    ) : (
                      <span className="text-muted-foreground">{formatCents(balance)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                    {po.source_estimate_number != null ? (
                      <Link
                        to={`/estimates/${po.source_estimate_id}`}
                        className="font-mono text-xs hover:underline underline-offset-4"
                      >
                        {po.source_estimate_number}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setLinkEstFor(po)}
                        className="text-muted-foreground text-xs hover:text-primary transition-colors"
                      >
                        Link
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <Link to={`/purchase-orders/${po.id}`}>
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                          View
                          <ChevronRight className="h-3 w-3 ml-0.5" />
                        </Button>
                      </Link>
                      {canEdit && (
                        <Link to={`/purchase-orders/${po.id}/edit`}>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                            <Pencil className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                        </Link>
                      )}
                      {isAdmin && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                          onClick={() => setDeletingPO(po)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Delete dialog */}
      {deletingPO && (
        <DeleteDialog
          po={deletingPO}
          onCancel={() => setDeletingPO(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}

      {/* Link estimate modal */}
      {linkEstFor && (
        <LinkEstimateToPoModal
          poId={linkEstFor.id}
          currentEstimate={null}
          onClose={() => setLinkEstFor(null)}
          onLinked={() => setLinkEstFor(null)}
        />
      )}
    </div>
  );
}
